import { countryOptions, regionLabelKeys, regions } from '@/helpers/CountriesAndRegions';
import { filterEntries, mapEntries } from '@/helpers/DictHelpers';
import { config } from '@/helpers/Environment';
import { upsertUserAndGetId } from '@/api/User';
import { languageItem } from '@/language/helpers';
import { isLoggedIn } from '@/state/Authentication';
export function builtInFields() {
    const t = languageItem('identityForm');
    const email = {
        label: t.email,
        type: 'email',
        required: true,
        autocomplete: 'email',
        maxlength: 254,
    };
    const password = {
        label: t.password,
        type: 'password',
        required: true,
        minlength: 8,
        maxlength: 254,
    };
    const result = {
        first_name: {
            label: t.firstName,
            type: 'text',
            required: true,
            maxlength: 50,
            autocomplete: 'given-name',
        },
        last_name: {
            label: t.lastName,
            type: 'text',
            required: true,
            maxlength: 50,
            autocomplete: 'family-name',
        },
        email: email,
        email_confirm: {
            ...email,
            label: t.emailConfirm,
            mustEqualCaseInsensitive: 'email',
        },
        password: password,
        password_confirm: {
            ...password,
            label: t.passwordConfirm,
            mustEqual: 'password',
        },
    };
    return mapEntries(result, (value, key) => {
        return { ...value, key };
    });
}
export function customFieldsDefaults() {
    return mapEntries(configIdentityFields(), (field) => field.default);
}
/** WCAG 1.3.5 autofill vocabulary keyed by well-known field keys.
 * Used as a fallback when the field's `format` doesn't already carry an `autocomplete` value — lets the `address` key (handled specially by IdentityForm/AddressInput) resolve without requiring tenant config changes.
 */
const fallbackAutocompleteAttrs = {
    address: 'street-address',
};
export function customFields(answers) {
    return mapEntries(configIdentityFields(), (field, key) => {
        const attributes = customFieldAttributes(field, answers);
        return {
            key,
            labelHtml: field.title,
            required: field.required,
            maxlength: 1000,
            ...attributes,
            autocomplete: attributes.autocomplete ?? fallbackAutocompleteAttrs[key],
        };
    });
}
export function configIdentityFields() {
    return config.custom_identity_fields || {};
}
function customFieldAttributes(field, answers) {
    const zipCodeFormat = {
        type: 'text',
        autocomplete: 'postal-code',
        // The space character (` `) is encoded (`\u0020`).
        pattern: '[A-Za-z0-9]+(\u0020|-)?[A-Za-z0-9]*',
        minlength: 4,
        maxlength: 10,
    };
    const formats = {
        email: { type: 'email' },
        phone: { type: 'phone-number', maxlength: 50, autocomplete: 'tel' },
        zip_code: zipCodeFormat,
        post_code: zipCodeFormat,
        country: {
            type: 'select',
            options: countryOptions,
            showPlaceholderDropdownOption: true,
            // This is the country code, as per https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#attr-fe-autocomplete-country
            autocomplete: 'country',
        },
        state: stateField(answers),
    };
    if (field.enum) {
        return {
            type: field.editor ?? 'options',
            showPlaceholderDropdownOption: true,
            enum: field.enum,
        };
    }
    if (field.type === 'boolean') {
        return { type: 'checkbox', label: field.description };
    }
    if (field.format && formats[field.format]) {
        return formats[field.format];
    }
    return { type: 'text' };
}
function stateField(answers) {
    const country = (answers.country || customFieldsDefaults().country);
    const key = regionLabelKeys[country] || 'state';
    const language = languageItem('identityForm');
    const required = regions[country] != null;
    return {
        labelHtml: language[key],
        type: 'select',
        options: regions[country],
        required: required,
        disabled: !required,
        showPlaceholderDropdownOption: true,
    };
}
export function getIdentityPayload(data) {
    return {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        password: data.password,
        additional_info: sanitizeIdentityMeta(data),
    };
}
export function upsertGuestAndGetId(data) {
    const payload = getIdentityPayload(data);
    return upsertUserAndGetId(payload, isLoggedIn());
}
/**
 * Ensures built in fields like password, email, first_name and last_name are not included in metadata.
 */
export function sanitizeIdentityMeta(data) {
    const builtIn = new Set(Object.keys(builtInFields()));
    return filterEntries(data, (_, key) => !builtIn.has(key));
}
